// Login.js

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button, TextField, Box, Typography, Container, Paper } from '@mui/material';
import Header from '../components/Header';
import SessionVerificationError from '../components/SessionVerificationError';

function signInErrorMessage(error, mfa = false) {
  if (error.code === 'auth/restart-sign-in') return error.message;
  if (error.code === 'auth/network-request-failed') {
    return 'Could not reach the sign-in service. Check your connection and try again.';
  }
  if (error.code === 'auth/too-many-requests') return 'Too many attempts. Wait a few minutes and try again.';
  if (error.code === 'auth/user-disabled') return 'This account is disabled. Contact your administrator.';
  if (mfa && error.code === 'auth/invalid-verification-code') return 'That verification code was not accepted. Enter the current code.';
  if (!mfa && ['auth/invalid-credential', 'auth/wrong-password', 'auth/user-not-found'].includes(error.code)) {
    return 'The email address or password was not accepted.';
  }
  return 'Could not complete sign-in. Please try again.';
}

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  // True from a submitted login until this page navigates away, so the
  // "already logged in" card does not flash while the post-login checks run.
  const [signingIn, setSigningIn] = useState(false);
  const {
    login,
    logout,
    currentUser,
    loading,
    mfaRequired,
    completeMfaSignIn,
    verifySession,
    sessionError,
    resetPassword
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  // Set by a page that signed the user out on purpose (MFASetup, when Firebase
  // demands a fresh sign-in) to explain why they are back here.
  const notice = location.state && location.state.notice;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSigningIn(true);
    try {
      const result = await login(email, password);
      if (result.mfaRequired) {
        setSigningIn(false);
        return;
      }
    } catch (error) {
      setSigningIn(false);
      setError(signInErrorMessage(error));
      return;
    }
    await handlePostLogin();
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSigningIn(true);
    try {
      await completeMfaSignIn(otpCode);
    } catch (error) {
      setSigningIn(false);
      setError(signInErrorMessage(error, true));
      setOtpCode('');
      return;
    }
    setOtpCode('');
    await handlePostLogin();
  };

  async function handlePostLogin() {
    setSigningIn(true);
    const session = await verifySession();
    if (session) {
      if (!session.emailVerified) navigate('/verify-email');
      else navigate(session.mfaEnrolled ? '/' : '/mfa-setup');
    } else {
      setSigningIn(false);
    }
  }

  const handleLogout = async () => {
    try {
      setError('');
      await logout();
      setOtpCode('');
      setSigningIn(false);
    } catch (error) {
      setError('Failed to log out. Please try again.');
      console.error(error);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    try {
      setError('');
      await resetPassword(resetEmail);
      setResetSent(true);
    } catch (error) {
      setError('Failed to send password reset email. Please check the email address.');
      console.error(error);
    }
  };

  // Password reset form
  if (showResetForm) {
    return (
      <div className="LoginPage">
        <Header />
        <Container maxWidth="sm" sx={{ mt: '6rem', mb: '2rem' }}>
          <Paper
            elevation={0}
            sx={{
              padding: '3rem',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              borderRadius: '12px'
            }}
          >
            <Typography
              variant="h3"
              align="center"
              sx={{ mb: 4, color: '#1F2937', fontWeight: 600 }}
            >
              Reset Password
            </Typography>
            {error && (
              <Typography
                align="center"
                sx={{
                  mb: 2,
                  color: '#EF4444',
                  backgroundColor: '#FEE2E2',
                  padding: '0.75rem',
                  borderRadius: '6px'
                }}
              >
                {error}
              </Typography>
            )}
            {resetSent ? (
              <>
                <Typography
                  align="center"
                  sx={{
                    mb: 3,
                    color: '#16A34A',
                    backgroundColor: '#F0FDF4',
                    padding: '0.75rem',
                    borderRadius: '6px'
                  }}
                >
                  Password reset email sent. Check your inbox.
                </Typography>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setShowResetForm(false);
                    setResetSent(false);
                    setResetEmail('');
                    setError('');
                  }}
                  sx={{ mt: 1 }}
                >
                  Back to Login
                </Button>
              </>
            ) : (
              <Box component="form" onSubmit={handlePasswordReset} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                <Typography align="center" sx={{ color: '#6B7280', mb: 1 }}>
                  Enter your email address and we'll send you a link to reset your password.
                </Typography>
                <TextField
                  type="email"
                  label="Email"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  fullWidth
                  variant="outlined"
                />
                <Button type="submit" variant="contained" color="primary" size="large" sx={{ mt: 1, py: 1.5 }}>
                  Send Reset Link
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setShowResetForm(false);
                    setError('');
                  }}
                  sx={{ py: 1.5 }}
                >
                  Back to Login
                </Button>
              </Box>
            )}
          </Paper>
        </Container>
      </div>
    );
  }

  if (sessionError) return <SessionVerificationError onRetry={handlePostLogin} />;

  // MFA verification form
  if (mfaRequired) {
    return (
      <div className="LoginPage">
        <Header />
        <Container maxWidth="sm" sx={{ mt: '6rem', mb: '2rem' }}>
          <Paper
            elevation={0}
            sx={{
              padding: '3rem',
              backgroundColor: '#FFFFFF',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
              borderRadius: '12px'
            }}
          >
            <Typography
              variant="h3"
              align="center"
              sx={{ mb: 2, color: '#1F2937', fontWeight: 600 }}
            >
              Two-Factor Authentication
            </Typography>
            <Typography
              align="center"
              sx={{ mb: 4, color: '#6B7280' }}
            >
              Enter the 6-digit code from your authenticator app.
            </Typography>
            {error && (
              <Typography
                align="center"
                sx={{
                  mb: 2,
                  color: '#EF4444',
                  backgroundColor: '#FEE2E2',
                  padding: '0.75rem',
                  borderRadius: '6px'
                }}
              >
                {error}
              </Typography>
            )}
            <Box component="form" onSubmit={handleMfaSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                type="text"
                label="Verification Code"
                value={otpCode}
                onChange={(e) => {
                  const val = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtpCode(val);
                }}
                required
                fullWidth
                variant="outlined"
                inputProps={{
                  maxLength: 6,
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  style: { textAlign: 'center', fontSize: '1.5rem', letterSpacing: '0.5rem' }
                }}
                autoFocus
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={otpCode.length !== 6 || signingIn}
                sx={{ mt: 2, py: 1.5, fontSize: '1rem', fontWeight: 500 }}
              >
                {signingIn ? 'Verifying...' : 'Verify'}
              </Button>
              <Button onClick={handleLogout} disabled={signingIn}>Back to sign-in</Button>
            </Box>
          </Paper>
        </Container>
      </div>
    );
  }

  return (
    <div className="LoginPage">
      <Header />
      <Container maxWidth="sm" sx={{ mt: '6rem', mb: '2rem' }}>
        <Paper
          elevation={0}
          sx={{
            padding: '3rem',
            backgroundColor: '#FFFFFF',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
            borderRadius: '12px'
          }}
        >
          <Typography
            variant="h3"
            align="center"
            sx={{
              mb: 4,
              color: '#1F2937',
              fontWeight: 600
            }}
          >
            Welcome Back
          </Typography>
          {notice && !currentUser && (
            <Typography
              align="center"
              sx={{
                mb: 2,
                color: '#1D4ED8',
                backgroundColor: '#DBEAFE',
                padding: '0.75rem',
                borderRadius: '6px'
              }}
            >
              {notice}
            </Typography>
          )}
          {error && (
            <Typography
              align="center"
              sx={{
                mb: 2,
                color: '#EF4444',
                backgroundColor: '#FEE2E2',
                padding: '0.75rem',
                borderRadius: '6px'
              }}
            >
              {error}
            </Typography>
          )}
          {!loading && !currentUser && (
            <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              <TextField
                type="email"
                id="email"
                label="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#2196F3',
                    },
                  },
                }}
              />
              <TextField
                type="password"
                id="password"
                label="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                fullWidth
                variant="outlined"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: '#2196F3',
                    },
                  },
                }}
              />
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                sx={{
                  mt: 2,
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500
                }}
                disabled={signingIn}
              >
                {signingIn ? 'Signing in...' : 'Log In'}
              </Button>
              <Typography
                align="center"
                sx={{
                  color: '#2196F3',
                  cursor: 'pointer',
                  fontSize: '0.875rem',
                  '&:hover': { textDecoration: 'underline' }
                }}
                onClick={() => {
                  setShowResetForm(true);
                  setError('');
                  setResetEmail(email);
                }}
              >
                Forgot your password?
              </Typography>
            </Box>
          )}
          {!loading && currentUser && !signingIn && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, alignItems: 'center' }}>
              <Typography align="center" sx={{ color: '#6B7280' }}>
                You are already logged in!
              </Typography>
              <Button variant="contained" onClick={handlePostLogin}>
                Continue
              </Button>
              <Button
                variant="outlined"
                size="large"
                onClick={handleLogout}
                sx={{ py: 1.5, px: 4, fontSize: '1rem', fontWeight: 500 }}
              >
                Log Out
              </Button>
            </Box>
          )}
        </Paper>
      </Container>
    </div>
  );
}

export default LoginPage;
